import { Button } from '@/components/Button';
import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { ContactForm } from '@/partials/dashboard/Contacts/ContactForm';
import { useAuthStore } from '@/stores/useAuthStore';
import useContactStore from '@/stores/useContactStore';
import i18n from '@/translations/config';
import { CreateContact } from '@/types/contact';
import { handleError } from '@/utils/handleError';
import { FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const UpdateContact = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { contact, showContact, updateContact, deleteContact, loading } =
    useContactStore();
  const { user } = useAuthStore();

  useEffect(() => {
    if (id && user?.id) {
      showContact(user.id, Number(id));
    }
  }, [id, user?.id, showContact]);

  const handleUpdateContact = async (
    values: CreateContact,
    { setErrors, setSubmitting }: FormikHelpers<CreateContact>,
  ) => {
    setIsSubmitting(true);
    setSubmitting(true);
    try {
      if (id && user?.id) {
        await updateContact(user.id, Number(id), values);
        // Ne pas rediriger après la mise à jour
      }
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleDeleteContact = async () => {
    if (id && user?.id) {
      await deleteContact(user.id, Number(id));
      navigate(`/${i18n.language}/dashboard/contacts`);
    }
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading || !contact) return <Loading />;

  const initialValues: CreateContact = {
    full_name: contact.full_name,
    email: contact.email || '',
    phone: contact.phone || '',
    company: contact.company || '',
    notes: contact.notes || '',
    source: contact.source || 'manual',
    user_id: contact.user_id || (user?.id as number),
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <FaArrowLeft className="size-4" />
            {t('common:back')}
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-secondary">
            {t('updateContact:title', { name: contact.full_name })}
          </h1>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="relative">
          <motion.div variants={itemVariants}>
            <Text className="text-center font-medium text-sky-800 md:text-left">
              {t('updateContact:information.title')}
            </Text>

            <ContactForm
              initialValues={initialValues}
              onSubmit={handleUpdateContact}
              isLoading={loading || isSubmitting}
              translationPrefix="updateContact"
              footer={
                <div className="mt-4 sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={loading || isSubmitting}
                    className="w-full lg:w-auto"
                  >
                    {t('updateContact:information.submit')}
                  </Button>
                </div>
              }
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <LineSeparator className="my-6" />
          </motion.div>

          {/* Section Suppression */}
          <motion.div variants={itemVariants}>
            <Text className="text-center font-medium text-sky-800 md:text-left">
              {t('updateContact:delete.title')}
            </Text>
            <Button
              className="mt-4 w-full bg-danger lg:w-auto"
              onClick={handleOpenModal}
            >
              {t('updateContact:delete.submit')}
            </Button>
          </motion.div>

          <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            className="w-auto"
            title={t('updateContact:delete.validation.title')}
          >
            <p className="mb-4">
              {t('updateContact:delete.validation.description')}
            </p>
            <div className="mt-4 flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
              <Button onClick={handleCloseModal}>{t('common:cancel')}</Button>
              <Button className="bg-danger" onClick={handleDeleteContact}>
                {t('common:confirm')}
              </Button>
            </div>
          </Modal>
        </div>
      </motion.div>
    </div>
  );
};
